import { useSelector } from 'react-redux';
import { useEffect } from 'react';
import { useResourcesService } from "../services";
import { useJwt } from "../helpers/common";

interface UserResource {
  id: number | string;
  name: string;
  email: string;
  skills: string[];
  busy: boolean;
  tasks: any[];
  image_url: string | null;
  skills_name: string[];
  cost_per_hour: number;
  billing_per_hour: number;
  sort_order: number;
  invite_status: string;
}

interface ResourceInfo {
  id: number | string;
  name: string;
  email?: string;
  image_url?: string | null;
  skills?: string[];
  skills_name?: string[];
  cost_per_hour?: number;
  billing_per_hour?: number;
  invite_status?: string;
  busy?: boolean;
}

export const useResourceInfo = () => {
  const { data: resourcesData = [], loading: resourcesLoading } = useSelector((state: any) => state.resources);
  const { fetchResources } = useResourcesService();
  const JWTdata: any = useJwt();
  const org_id = JWTdata.org_id;

  useEffect(() => {
    const fetchData = async () => {
      if (org_id && !resourcesData.length && !resourcesLoading) {
        await fetchResources(org_id);
      }
    };
    fetchData();
  }, [org_id, resourcesData.length, resourcesLoading]);

  const getResourceInfo = (resourceId: string | number, projectData?: any): ResourceInfo => {
    if (!resourceId) {
      return {
        id: resourceId,
        name: '-'
      };
    }

    // First check project members
    if (projectData?.members?.length) {
      const memberResource = projectData.members.find(
        (member: any) => String(member.id) === String(resourceId)
      );
      if (memberResource) {
        return {
          id: memberResource.id,
          name: memberResource.name,
          email: memberResource.email,
          image_url: memberResource.image_url,
          skills: memberResource.skills,
          skills_name: memberResource.skills_name,
          cost_per_hour: memberResource.cost_per_hour,
          billing_per_hour: memberResource.billing_per_hour,
          invite_status: memberResource.invite_status,
          busy: memberResource.busy
        };
      }
    }

    // Then check global resources
    const globalResource = resourcesData?.find(
      (resource: UserResource) => String(resource.id) === String(resourceId)
    );

    if (globalResource) {
      return {
        id: globalResource.id,
        name: globalResource.name || String(resourceId),
        email: globalResource.email,
        image_url: globalResource.image_url,
        skills: globalResource.skills,
        skills_name: globalResource.skills_name,
        cost_per_hour: globalResource.cost_per_hour,
        billing_per_hour: globalResource.billing_per_hour,
        invite_status: globalResource.invite_status,
        busy: globalResource.busy
      };
    }

    // Return basic info if user not found
    return {
      id: resourceId,
      name: String(resourceId)
    };
  };

  // Keep getResourceName for backward compatibility
  const getResourceName = (resourceId: string | number, projectData?: any): string => {
    const resourceInfo = getResourceInfo(resourceId, projectData);
    return resourceInfo.name;
  };

  return {
    resourcesData,
    resourcesLoading,
    getResourceName,
    getResourceInfo,
    org_id
  };
}; 